import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { Provider } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import { store } from './store';


import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import ProductsPage from './pages/ProductsPage';
import AnalysesPage from './pages/AnalysesPage';
import ReviewQueuePage from './pages/ReviewQueuePage';
import BrandsPage from './pages/BrandsPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import AnalysisDetailsPage from './pages/AnalysisDetailsPage';
import BulkAnalysisPage from './pages/EnhancedAnalysisPage';
import AnalyticsPage from './pages/AnalyticsPage';
import BulkAnalysisResultsPage from './pages/BulkAnalysisResultsPage';


const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb', // Modern blue
      light: '#3b82f6',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7c3aed', // Modern purple
      light: '#8b5cf6',
      dark: '#6d28d9',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // Light gray background
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b', // Dark slate
      secondary: '#64748b', // Medium slate
    },
    error: {
      main: '#ef4444', // Modern red
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b', // Modern amber
      light: '#fbbf24',
      dark: '#d97706',
    },
    success: {
      main: '#10b981', // Modern emerald
      light: '#34d399',
      dark: '#059669',
    },
    info: {
      main: '#06b6d4', // Modern cyan
      light: '#22d3ee',
      dark: '#0891b2',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.025em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.025em',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.025em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
    '0px 25px 50px rgba(0, 0, 0, 0.1), 0px 10px 20px rgba(0, 0, 0, 0.04)',
    '0px 35px 60px rgba(0, 0, 0, 0.1), 0px 15px 25px rgba(0, 0, 0, 0.04)',
    '0px 45px 70px rgba(0, 0, 0, 0.1), 0px 20px 30px rgba(0, 0, 0, 0.04)',
    '0px 55px 80px rgba(0, 0, 0, 0.1), 0px 25px 35px rgba(0, 0, 0, 0.04)',
    '0px 65px 90px rgba(0, 0, 0, 0.1), 0px 30px 40px rgba(0, 0, 0, 0.04)',
    '0px 75px 100px rgba(0, 0, 0, 0.1), 0px 35px 45px rgba(0, 0, 0, 0.04)',
    '0px 85px 110px rgba(0, 0, 0, 0.1), 0px 40px 50px rgba(0, 0, 0, 0.04)',
    '0px 95px 120px rgba(0, 0, 0, 0.1), 0px 45px 55px rgba(0, 0, 0, 0.04)',
    '0px 105px 130px rgba(0, 0, 0, 0.1), 0px 50px 60px rgba(0, 0, 0, 0.04)',
    '0px 115px 140px rgba(0, 0, 0, 0.1), 0px 55px 65px rgba(0, 0, 0, 0.04)',
    '0px 125px 150px rgba(0, 0, 0, 0.1), 0px 60px 70px rgba(0, 0, 0, 0.04)',
    '0px 135px 160px rgba(0, 0, 0, 0.1), 0px 65px 75px rgba(0, 0, 0, 0.04)',
    '0px 145px 170px rgba(0, 0, 0, 0.1), 0px 70px 80px rgba(0, 0, 0, 0.04)',
    '0px 155px 180px rgba(0, 0, 0, 0.1), 0px 75px 85px rgba(0, 0, 0, 0.04)',
    '0px 165px 190px rgba(0, 0, 0, 0.1), 0px 80px 90px rgba(0, 0, 0, 0.04)',
    '0px 175px 200px rgba(0, 0, 0, 0.1), 0px 85px 95px rgba(0, 0, 0, 0.04)',
    '0px 185px 210px rgba(0, 0, 0, 0.1), 0px 90px 100px rgba(0, 0, 0, 0.04)',
    '0px 195px 220px rgba(0, 0, 0, 0.1), 0px 95px 105px rgba(0, 0, 0, 0.04)',
    '0px 205px 230px rgba(0, 0, 0, 0.1), 0px 100px 110px rgba(0, 0, 0, 0.04)',
  ],
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1e293b',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e2e8f0',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          '&:hover': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          padding: '8px 16px',
          '&:hover': {
            transform: 'translateY(-1px)',
            transition: 'transform 0.2s ease-in-out',
          },
        },
        contained: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: 'rgba(37, 99, 235, 0.08)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: 'rgba(37, 99, 235, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(37, 99, 235, 0.12)',
            color: '#2563eb',
            '&:hover': {
              backgroundColor: 'rgba(37, 99, 235, 0.16)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2563eb',
            },
          },
        },
      },
    },
  },
});


const App: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider 
          maxSnack={3}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Router>
              <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
                <Navbar onSidebarToggle={handleSidebarToggle} />
                <Sidebar open={sidebarOpen} onToggle={handleSidebarToggle} />
                
                <Box
                  component="main"
                  sx={{
                    flexGrow: 1,
                    p: 3,
                    mt: 8, // Account for AppBar height
                    transition: theme.transitions.create(['margin'], {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.leavingScreen,
                    }),
                  }}
                >
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/analyses" element={<AnalysesPage />} />
                    <Route path="/analyses/:analysisId" element={<AnalysisDetailsPage />} />
                    <Route path="/enhanced-analysis" element={<BulkAnalysisPage />} />
                    <Route path="/bulk-analysis-results/:queryId" element={<BulkAnalysisResultsPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/review-queue" element={<ReviewQueuePage />} />
                    <Route path="/brands" element={<BrandsPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </Box>
              </Box>
            </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </Provider>
  );
};

export default App; 